import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { milestoneApi } from '../../services/api';
import { apiErrorCode, saveErrorText } from '../../utils/apiError';
import {
  addPlanRow,
  computePlan,
  hasPlanErrors,
  hasStageAmounts,
  initialPlanState,
  movePlanRow,
  planFingerprint,
  splitEvenly,
  switchDistribution,
  switchPricingMode,
  toPlanPayload,
  validatePlan,
  withAutofill,
  type DistributionMode,
  type PlanRow,
  type PlanState,
  type PricingMode,
} from './milestonePlan';
import type { WriteGuard } from './useContractWizardDraft';

/**
 * State and persistence for wizard step 2. Saving replaces the contract's
 * milestone plan in one call; the API requires every existing (unpaid) stage
 * in the plan, so stages the user removed are deleted first, and new stages
 * get their server ids back afterwards so the next save updates them.
 */
export function useMilestonePlanStep(contractId: string | undefined) {
  const { t } = useTranslation();
  const [state, setState] = useState<PlanState>(initialPlanState);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ text: '', at: 0 });
  /**
   * Set when switching pricing model emptied the stages' amounts: the model the
   * plan was switched to, so the step can say what to enter now. Cleared when an
   * amount is entered, the model is switched again, or the plan is (re)loaded.
   */
  const [amountsCleared, setAmountsCleared] = useState<PricingMode | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const removedIdsRef = useRef<string[]>([]);
  const savedFingerprintRef = useRef(planFingerprint(state));
  const writeGuardRef = useRef<WriteGuard | null>(null);
  const editingRef = useRef(false);
  /** The save in progress; a new save waits for it (ClickUp 869fh1kfj). */
  const savingRef = useRef<Promise<boolean> | null>(null);
  /** Set once the plan has been written, so an unchanged plan is not written again. */
  const savedOnceRef = useRef(false);

  const update = useCallback((next: (previous: PlanState) => PlanState) => {
    setState(previous => {
      const value = next(previous);
      stateRef.current = value;
      return value;
    });
    setSaveError('');
  }, []);

  /**
   * Replaces the plan with `next` and treats it as saved. `editing`: the plan of
   * an existing contract (save status reads "Ändringar sparade").
   */
  const load = useCallback((next: PlanState, editing = false) => {
    editingRef.current = editing;
    savedOnceRef.current = false;
    stateRef.current = next;
    setState(next);
    removedIdsRef.current = [];
    savedFingerprintRef.current = planFingerprint(next);
    setShowErrors(false);
    setSaveError('');
    setSaveStatus({ text: '', at: 0 });
    setAmountsCleared(null);
  }, []);

  const reset = useCallback(() => {
    writeGuardRef.current = null;
    load(initialPlanState());
  }, [load]);

  /** Same contract as the draft hook: writes wait for the guard while it is set. */
  const setWriteGuard = useCallback((guard: WriteGuard | null) => {
    writeGuardRef.current = guard;
  }, []);

  /** Switching model empties the stages' amounts (and says so) when there were any. */
  const setPricingMode = (mode: PricingMode) => {
    const previous = stateRef.current;
    if (previous.pricingMode === mode) return;
    setAmountsCleared(hasStageAmounts(previous) ? mode : null);
    update(current => switchPricingMode(current, mode));
  };
  const setDistribution = (mode: DistributionMode) => update(previous => switchDistribution(previous, mode));
  const setTotal = (value: string) => update(previous => ({ ...previous, total: value }));
  const setAfterRot = (value: string) => update(previous => ({ ...previous, afterRot: value }));
  const setRow = (key: string, patch: Partial<PlanRow>) => {
    if ([patch.amount, patch.rotAmount, patch.percent].some(value => value !== undefined && value.trim() !== '')) {
      setAmountsCleared(null);
    }
    update(previous => ({ ...previous, rows: previous.rows.map(row => (row.key === key ? { ...row, ...patch } : row)) }));
  };
  /** "Använd 67 500 kr": the suggested remainder on the last stage becomes the user's value. */
  const acceptSuggestion = () => {
    setAmountsCleared(null);
    update(withAutofill);
  };
  const addRow = () => update(previous => addPlanRow(previous));
  /** Moves a stage one step up (-1) or down (+1); the stages are renumbered by position. */
  const moveRow = (key: string, direction: -1 | 1) => update(previous => movePlanRow(previous, key, direction));
  const removeRow = (key: string) =>
    update(previous => {
      if (previous.rows.length <= 1) return previous;
      const removed = previous.rows.find(row => row.key === key);
      if (removed?.id) removedIdsRef.current = [...removedIdsRef.current, removed.id];
      return { ...previous, rows: previous.rows.filter(row => row.key !== key) };
    });
  const distributeEvenly = () => {
    // Nothing is filled in while there is no amount to split: keep the notice then.
    if (splitEvenly(stateRef.current) !== stateRef.current) setAmountsCleared(null);
    update(splitEvenly);
  };

  const saveNow = useCallback(async (): Promise<boolean> => {
    if (!contractId) return false;
    // An unchanged plan is not written again (and nothing is withdrawn for it):
    // in edit mode, or once it has been saved, e.g. by a save that ran just before.
    if (
      (editingRef.current || savedOnceRef.current) &&
      planFingerprint(stateRef.current) === savedFingerprintRef.current &&
      removedIdsRef.current.length === 0
    ) {
      return true;
    }
    if (writeGuardRef.current && !(await writeGuardRef.current())) return false;
    setSaving(true);
    setSaveError('');
    setSaveStatus({ text: t('contracts.wizard.saveStatus.saving'), at: Date.now() });
    try {
      // Stages removed in the wizard must go before the plan is replaced. Each
      // one leaves the list as soon as it is deleted, so it is never deleted twice.
      for (const id of [...removedIdsRef.current]) {
        await milestoneApi.deleteMilestone(id);
        removedIdsRef.current = removedIdsRef.current.filter(removed => removed !== id);
      }
      const current = stateRef.current;
      await milestoneApi.replaceMilestonePlan(contractId, toPlanPayload(current));

      // Give new rows their server ids (new stages are numbered after the known ones).
      const known = new Set(current.rows.flatMap(row => (row.id ? [row.id] : [])));
      const created = (await milestoneApi.getMilestones(contractId))
        .filter(milestone => !known.has(milestone.id) && milestone.deliveryStatus !== 'paid')
        .sort((left, right) => left.milestoneNumber - right.milestoneNumber);
      let next = 0;
      const withIds: PlanState = {
        ...current,
        rows: current.rows.map(row => (row.id ? row : { ...row, id: created[next++]?.id })),
      };
      stateRef.current = withIds;
      setState(withIds);
      savedFingerprintRef.current = planFingerprint(withIds);
      savedOnceRef.current = true;
      setSaveError('');
      setSaveStatus({
        text: t(editingRef.current ? 'contracts.wizard.saveStatus.savedChanges' : 'contracts.wizard.saveStatus.saved', {
          time: new Date().toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }),
        }),
        at: Date.now(),
      });
      return true;
    } catch (error) {
      console.error('Failed to save milestone plan:', error);
      setSaveStatus({ text: t('contracts.wizard.saveStatus.error'), at: Date.now() });
      const code = apiErrorCode(error);
      setSaveError(
        code === 'MILESTONE_PLAN_CHANGED'
          ? t('projectView.wizard.errPlanChanged')
          : code?.startsWith('ROT_ALLOCATION')
            ? t('contracts.wizard.pricing.rotAllocationError')
            : saveErrorText(t, error, 'milestones.form.saveError'),
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [contractId, t]);

  /**
   * Saves run one at a time (ClickUp 869fh1kfj): "Spara ändringar", "Nästa" and
   * the step indicator can all ask for a save at once. Two parallel saves sent
   * the same plan; the second lacked the new stage's id and got
   * MILESTONE_PLAN_CHANGED (409), or deleted the same stage twice (404). A later
   * save now waits for the one in progress and then writes only what changed.
   */
  const save = useCallback(async (): Promise<boolean> => {
    while (savingRef.current) {
      await savingRef.current.catch(() => false);
    }
    const run = saveNow();
    savingRef.current = run;
    try {
      return await run;
    } finally {
      if (savingRef.current === run) savingRef.current = null;
    }
  }, [saveNow]);

  /** "Spara ändringar" with an incomplete plan writes nothing: say so instead of keeping an old "saved" status. */
  const noteIncompletePlan = useCallback(() => {
    setSaveStatus({ text: t('contracts.wizard.saveStatus.planIncomplete'), at: Date.now() });
  }, [t]);

  /** "Nästa" on step 2: show what is wrong, otherwise save the plan and continue. */
  const validateAndSave = useCallback(async () => {
    if (hasPlanErrors(validatePlan(stateRef.current))) {
      setShowErrors(true);
      return false;
    }
    setShowErrors(false);
    if (writeGuardRef.current) return true;
    return save();
  }, [save]);

  const errors = validatePlan(state);
  const plan = computePlan(state);
  const dirty = planFingerprint(state) !== savedFingerprintRef.current || removedIdsRef.current.length > 0;

  return {
    state,
    plan,
    errors,
    showErrors,
    saveError,
    saving,
    saveStatus: saveStatus.text,
    saveStatusAt: saveStatus.at,
    dirty,
    amountsCleared,
    setPricingMode,
    setDistribution,
    setTotal,
    setAfterRot,
    setRow,
    addRow,
    moveRow,
    removeRow,
    distributeEvenly,
    acceptSuggestion,
    save,
    noteIncompletePlan,
    validateAndSave,
    reset,
    load,
    setWriteGuard,
  };
}

export type MilestonePlanStepState = ReturnType<typeof useMilestonePlanStep>;
